import { SortDestinationColor } from "./schema/sort-destination";

export const SortDestinationForeground = {
  light: "light",
  dark: "dark",
} as const;
export type SortDestinationForeground =
  (typeof SortDestinationForeground)[keyof typeof SortDestinationForeground];

/**
 * Shown when a routed destination has no configured color: the hardcoded
 * "Warehouse"/"Quarantine" buckets and any unbound disposition. A neutral gray
 * so the interstitial still renders a full card rather than a broken empty one.
 */
export const DEFAULT_SORT_DESTINATION_COLOR = "#6B7280";

export interface SortDestinationDisplay {
  label: string;
  color: string;
  foreground: SortDestinationForeground;
  /** The Warehouse/Quarantine fallback bucket, not an operator-directed sort. */
  placeholder: boolean;
}

/**
 * A real routing target (bound tote or configured destination) worth surfacing
 * in the sort interstitial — as opposed to the Warehouse/Quarantine placeholder.
 */
export function isRoutableDestination(
  display: SortDestinationDisplay | null | undefined,
): display is SortDestinationDisplay {
  return display != null && !display.placeholder;
}

/**
 * Pick a legible text/icon color for an arbitrary merchant-chosen bin color.
 * YIQ perceived brightness against the mid threshold — dark ink on light bins,
 * light ink on dark bins. Unparseable input falls back to light ink.
 */
export function resolveSortDestinationForeground(
  color: string,
): SortDestinationForeground {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(color.trim());
  if (!match) {
    return SortDestinationForeground.light;
  }
  const hex = match[1]!;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 128
    ? SortDestinationForeground.dark
    : SortDestinationForeground.light;
}

/**
 * A pale tint of the bin color for banner backgrounds — blended most of the way
 * to white so any configured color stays light enough for dark text to read.
 */
export function lightSortDestinationTint(color: string): string {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(color.trim());
  if (!match) {
    return "#FFFFFF";
  }
  const hex = match[1]!;
  const toward = (channel: number) =>
    Math.round(channel + (255 - channel) * 0.85);
  const r = toward(parseInt(hex.slice(0, 2), 16));
  const g = toward(parseInt(hex.slice(2, 4), 16));
  const b = toward(parseInt(hex.slice(4, 6), 16));
  return `rgb(${r}, ${g}, ${b})`;
}

export function buildSortDestinationDisplay(
  label: string,
  color?: string | null,
): SortDestinationDisplay {
  const resolved = color ?? DEFAULT_SORT_DESTINATION_COLOR;
  return {
    label,
    color: resolved,
    foreground: resolveSortDestinationForeground(resolved),
    placeholder: false,
  };
}

/** The Warehouse/Quarantine bucket shown when nothing real routes the unit. */
export function buildPlaceholderDestination(
  label: string,
): SortDestinationDisplay {
  return { ...buildSortDestinationDisplay(label), placeholder: true };
}

/** Where an item goes when the warehouse owes work on it before it can sort. */
export const NEEDS_WORK_DESTINATION_LABEL = "Needs Work";

export function buildNeedsWorkDestination(): SortDestinationDisplay {
  return buildSortDestinationDisplay(
    NEEDS_WORK_DESTINATION_LABEL,
    SortDestinationColor.orange,
  );
}

export const SERVICE_REQUESTED_DESTINATION_LABEL = "Service Requested";

const SERVICE_REQUESTED_DESTINATION_COLOR = SortDestinationColor.yellow;

export function buildServiceRequestedDestination(): SortDestinationDisplay {
  return buildSortDestinationDisplay(
    SERVICE_REQUESTED_DESTINATION_LABEL,
    SERVICE_REQUESTED_DESTINATION_COLOR,
  );
}
